import { useMemo } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Crown, Edit, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Loader from '@/components/Loader';
import {
  capitalizeEveryWord,
  capitalizeFirstLetter,
  convertISODateToMMDDYYYY,
  displayFullName,
  formatPhoneNumber,
} from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsList } from '@/components/ScrollableTabsList';
import { useGetUserById } from '@/services/userService';
import { useUserStore } from '@/stores/user';
import { isAdminLikeRole } from '@/lib/roles';
import { UserLoginHistory } from './UserLoginHistory';

export const UserDetails = ({
  open,
  onCloseClick,
  userId,
}: {
  open: boolean;
  onCloseClick: () => void;
  userId: string;
}) => {
  const navigate = useNavigate();

  const { data: userData, isFetching, isError } = useGetUserById(userId);
  const viewerRoleId = useUserStore((state) => state.user?.roleId);
  // Login Activity is admin-only (DGR-1835); other roles keep the single details view.
  const showLoginActivity = isAdminLikeRole(viewerRoleId);

  const userDetail = useMemo(() => {
    if (userData) {
      const details = userData;
      return [
        {
          label: 'Email Address',
          value: details.email ? details.email : '-',
        },

        ...(details.roleId === 'SERVER'
          ? [
              {
                label: 'Phone Number',
                value: details.phone ? formatPhoneNumber(details.phone) : '-',
              },
              {
                label: 'Address',
                value: `${details.primaryAddress ? details.primaryAddress + ', ' : ''}${details.secondaryAddress ? details.secondaryAddress + ', ' : ''}${details.city ? details.city + ', ' : ''}${details.state ? details.state + ', ' : ''}${details.zipCode ? details.zipCode : ''}`,
              },
              {
                label: 'Secondary Email Address',
                value: details.secondaryEmail ? details.secondaryEmail : '-',
              },
            ]
          : []),

        {
          label: 'User ID',
          value: details.code ? details.code : '-',
        },
        {
          label: 'Team(s)',
          value: details.teams.length ? (
            <div className="flex flex-col gap-1 items-end">
              {details.teams.map((team) => (
                <div key={team.id} className="flex items-center gap-1">
                  {team.supervisorId === userId && <Crown size={14} className="text-yellow-500" />}
                  <span>{capitalizeEveryWord(team.name)}</span>
                </div>
              ))}
            </div>
          ) : (
            '-'
          ),
        },
        {
          label: 'Supervisor',
          value: details.teams.filter((team) => team.supervisorId === userId).length ? 'Yes' : 'No',
        },
        {
          label: 'Created On',
          value: convertISODateToMMDDYYYY(details.createdAt) ? convertISODateToMMDDYYYY(details.createdAt) : '-',
        },
        {
          ...(details.deactivatedAt && {
            label: 'Deactivated On',
            value: convertISODateToMMDDYYYY(details.deactivatedAt)
              ? convertISODateToMMDDYYYY(details.deactivatedAt)
              : '-',
          }),
        },
      ];
    }
  }, [userData]);

  const detailRows = (
    <div className="flex gap-4 flex-col w-full">
      {userDetail?.map((userDetail, index) => (
        <div key={index} className="grid grid-cols-2 justify-between items-start">
          <div className="font-medium flex-shrink-0 text-black">{userDetail.label}</div>
          <div
            className="text-end font-normal  text-black"
            style={{
              overflowWrap: 'anywhere',
            }}
          >
            {userDetail.value}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Sheet modal={false} open={open}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] overscroll-auto scrollbar-hide focus:outline-none py-6 px-5"
        tabIndex={-1}
      >
        <Loader isLoading={isFetching}>
          {isError ? (
            <div className="flex h-full items-center relative justify-center w-full text-placeholder">
              <div className="border-[1px] p-[6px] absolute top-0 right-0 rounded-md" onClick={onCloseClick}>
                <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" data-testid="close-icon" />
              </div>
              <span>No Results</span>
            </div>
          ) : (
            <SheetHeader>
              <SheetTitle className="flex flex-col gap-4">
                <div className="flex flex-row justify-between align-middle">
                  <div className="flex flex-row gap-2">
                    <div className="bg-[#9747FF33] rounded-md text-normal font-semibold p-2 h-[48px] min-w-[48px] w-fit flex justify-center text-[#55288F] ">
                      <span>
                        {userData?.firstName[0]}
                        {userData?.lastName[0]}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-base font-md">{displayFullName(userData)}</span>
                      <span className="text-sm font-normal">{capitalizeFirstLetter(userData?.roleId)}</span>
                    </div>
                  </div>
                  <div className="flex flex-row gap-4 h-[32px]">
                    {userData?.status && ['ACTIVE', 'INACTIVE'].includes(userData?.status) && (
                      <div
                        className="border-[1px] p-[6px] rounded-md justify-center"
                        onClick={() => navigate(`/users/update/${userId}`)}
                      >
                        <Edit strokeWidth={1} className="h-[20px] text-placeholder w-[20px] cursor-pointer" />
                      </div>
                    )}

                    <div className="border-[1px] p-[6px] rounded-md" onClick={onCloseClick}>
                      <X
                        strokeWidth={1}
                        className="h-[20px] w-[20px] text-placeholder cursor-pointer"
                        data-testid="close-icon"
                      />
                    </div>
                  </div>
                </div>
                <div className="text-[#969696] font-[400] flex flex-col text-sm">
                  {userData?.status === 'ACTIVE' ? (
                    <Badge className={`bg-[#D5F4E3] text-[#1B8652] hover:bg-[#D5F4E3] `}>Active</Badge>
                  ) : userData?.status === 'INACTIVE' ? (
                    <Badge className={`bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5] `}>Inactive</Badge>
                  ) : userData?.status === 'PENDING' ? (
                    <Badge className={`bg-[#ffeebf] text-[#896010] hover:bg-[#ffeebf]`}>Pending</Badge>
                  ) : (
                    <Badge className={`bg-[#FDEBEB] hover:bg-[#FDEBEB] text-error `}>Expired</Badge>
                  )}
                </div>
                {!showLoginActivity && (
                  <div className="py-4">
                    <hr />
                  </div>
                )}
              </SheetTitle>
              <SheetDescription />
              <div className="w-full">
                {showLoginActivity ? (
                  <Tabs defaultValue="USER_DETAILS" className="w-full text-black">
                    <ScrollableTabsList className="!mt-0">
                      <TabsTrigger value="USER_DETAILS" className="flex-none w-1/2">
                        User Details
                      </TabsTrigger>
                      <TabsTrigger value="LOGIN_ACTIVITY" className="flex-none w-1/2">
                        Login Activity
                      </TabsTrigger>
                    </ScrollableTabsList>
                    <TabsContent value="USER_DETAILS" className="mt-4">
                      {detailRows}
                    </TabsContent>
                    <TabsContent value="LOGIN_ACTIVITY" className="mt-4">
                      <UserLoginHistory userId={userId} />
                    </TabsContent>
                  </Tabs>
                ) : (
                  detailRows
                )}
              </div>
            </SheetHeader>
          )}
        </Loader>
      </SheetContent>
    </Sheet>
  );
};
